import { $t } from '@/plugins/fluent';
import { DateTime } from 'luxon';
import { type Component, computed, ComputedRef, ref, Ref } from 'vue';
import { RouteLocationRaw } from 'vue-router';
import {
    AllocationCount,
    countAllocationsByLocation,
    countOwnAndForeignAllocationsByLocation,
    OwnAndForeignAllocationCount,
    requireAllocationCount,
} from '../logic/allocationCounts';
import { getIcon, getIconTitle } from '../logic/icons';
import { countConflicts } from '../logic/conflictCounts';
import { recalculateConflictMarkerShapes } from '../logic/conflictMarkers';
import { useLocationSelectionStore } from '../LocationSelection.store';
import { Allocation, ConflictMarker, LocationTypeCategory } from '../Timeline.types';
import { nestedLocationsErrorMessage, TimelineLocationData } from './useTimelineLocations';

type ConflictsCount = {
    resolved: number;
    unresolved: number;
    total: number;
};

/**
 * A flattened hierarchy row for the first column. Non-leaf rows are expandable and roll up their
 * descendants' conflict counts; leaf rows carry their own. `isVisible` reflects whether every ancestor is
 * expanded — collapsed rows stay in the list (and mounted via `v-show`) so selection registration
 * over collapsed branches keeps working.
 */
type HierarchyLocationRowDataBase = {
    id: string;
    label: string;
    level: number;
    typeCategory: LocationTypeCategory;
    isLeafLocation: boolean;
    isVisible: boolean;
    parentId: string | undefined;
    errorMessage: string | undefined;
    expandable:
        | {
              isExpandable: true;
              isExpanded: boolean;
              nestedConflictsCount: ConflictsCount;
              /**
               * When set, this parent location is an ancestor of the selection source, so collapsing it
               * would hide the source: the expand/collapse control is disabled and shows this tooltip.
               */
              expandableDisabledExplanation?: string;
          }
        | { isExpandable: false; conflictsCount: ConflictsCount };
    icon: { component: Component; title: string };
    linkTarget?: RouteLocationRaw;
    // Consumed when building the matching day (layout) rows.
    allocations: Allocation[];
    conflictMarkers: Map<string, ConflictMarker>;
};

export type HierarchyLocationRowData = HierarchyLocationRowDataBase & { allocationCount: AllocationCount };

export type OwnAndForeignHierarchyLocationRowData = HierarchyLocationRowDataBase & {
    allocationCount: OwnAndForeignAllocationCount;
};

type HierarchyComputedData = {
    conflictsCount: ConflictsCount;
    /** Allocations assigned directly to this location (not nested descendants). */
    directAllocations: Allocation[];
    /** Allocations assigned to nested descendants only — what a non-leaf row aggregates into bars. */
    nestedAllocations: Allocation[];
    /** Allocations assigned to this location and all nested descendants. */
    allAllocations: Allocation[];
    /** Conflict markers for this location's direct allocations only (used for leaf rows). */
    conflictMarkers: Map<string, ConflictMarker>;
    /** Conflict markers merged from this location and all nested descendants (used for non-leaf backgrounds). */
    allConflictMarkers: Map<string, ConflictMarker>;
};

/**
 * The render data for a single location that does not depend on which rows are expanded.
 */
type LocationRenderData = {
    containedAllocations: Allocation[];
    conflictsCount: ConflictsCount;
    icon: { component: Component; title: string };
    errorMessage: string | undefined;
    /** Final day-row allocations: direct allocations for leaves, aggregation bars for non-leaf rows. */
    allocations: Allocation[];
    /** Final day-row conflict markers: direct for leaves, merged descendants for non-leaf rows. */
    conflictMarkers: Map<string, ConflictMarker>;
};

// Recursively collect the allocations and conflicts of a location and its nested locations.
// `cache` is scoped to a single flatten run so each location is computed at most once.
function computeHierarchyData(
    location: TimelineLocationData,
    allocsByLoc: Map<string, Allocation[]>,
    conflictsByLoc: Map<string, Map<string, ConflictMarker>>,
    start: DateTime,
    end: DateTime,
    cache: Map<string, HierarchyComputedData>,
): HierarchyComputedData {
    const cached = cache.get(location.id);
    if (cached) {
        return cached;
    }

    const directAllocations = allocsByLoc.get(location.id) ?? [];
    const directConflictMarkers = conflictsByLoc.get(location.id) ?? new Map<string, ConflictMarker>();
    const directConflictCount = countConflicts(directConflictMarkers);

    // If leaf, just return direct counts
    if (location.isLeafLocation) {
        const result: HierarchyComputedData = {
            conflictsCount: { resolved: 0, unresolved: directConflictCount, total: directConflictCount },
            directAllocations,
            nestedAllocations: [],
            allAllocations: directAllocations,
            conflictMarkers: directConflictMarkers,
            allConflictMarkers: directConflictMarkers,
        };
        cache.set(location.id, result);
        return result;
    }

    // Aggregate from nested locations
    let nestedConflicts = directConflictCount;
    const allNestedAllocations: Allocation[] = [];
    // Union of every conflicting day across this location and its descendants. Shapes are recomputed from
    // this set below so adjacent conflicts from different locations merge into a single marker group.
    const conflictDates = new Set<string>(directConflictMarkers.keys());

    for (const nested of location.nestedLocations) {
        const nestedData = computeHierarchyData(nested, allocsByLoc, conflictsByLoc, start, end, cache);
        nestedConflicts += nestedData.conflictsCount.total;
        allNestedAllocations.push(...nestedData.allAllocations);
        for (const date of nestedData.allConflictMarkers.keys()) {
            conflictDates.add(date);
        }
    }

    const result: HierarchyComputedData = {
        conflictsCount: { resolved: 0, unresolved: nestedConflicts, total: nestedConflicts },
        directAllocations,
        nestedAllocations: allNestedAllocations,
        allAllocations: [...directAllocations, ...allNestedAllocations],
        conflictMarkers: directConflictMarkers,
        allConflictMarkers: recalculateConflictMarkerShapes(conflictDates),
    };
    cache.set(location.id, result);
    return result;
}

// Builds synthetic aggregation Allocation objects for a non-leaf location row.
// Clamps each allocation to the visible range, merges overlapping/adjacent spans,
// then produces one Aggregation allocation per contiguous block.
function computeAggregationAllocations(
    allAllocations: Allocation[],
    locationId: string,
    locationTypeCategory: LocationTypeCategory,
    start: DateTime,
    end: DateTime,
): Allocation[] {
    if (allAllocations.length === 0) {
        return [];
    }

    // Pre-compute bleed flags once rather than re-scanning per interval
    const anyStartsBefore = allAllocations.some((a) => a.startDateTime < start);
    const anyEndsAfter = allAllocations.some((a) => a.endDateTime > end);

    // Clamp each allocation to the visible range.
    // All allocations are pre-filtered to overlap with [start, end), so clamping always yields a valid interval.
    const intervals = allAllocations
        .map((alloc) => ({
            start: alloc.startDateTime < start ? start : alloc.startDateTime,
            end: alloc.endDateTime > end ? end : alloc.endDateTime,
        }))
        .sort((a, b) => a.start.toMillis() - b.start.toMillis());

    // Merge overlapping/adjacent intervals.
    const merged: Array<{ start: DateTime; end: DateTime }> = [intervals[0]];
    for (let i = 1; i < intervals.length; i++) {
        const current = intervals[i];
        const last = merged[merged.length - 1];
        if (current.start.toMillis() <= last.end.toMillis()) {
            if (current.end > last.end) {
                merged[merged.length - 1] = { start: last.start, end: current.end };
            }
        } else {
            merged.push(current);
        }
    }

    return merged.map((interval) => {
        // Extend bar by one day for allocations that bleed outside the visible range
        const barStart =
            anyStartsBefore && interval.start.toMillis() === start.toMillis()
                ? interval.start.minus({ days: 1 })
                : interval.start;
        const barEnd =
            anyEndsAfter && interval.end.toMillis() === end.toMillis() ? interval.end.plus({ days: 1 }) : interval.end;
        const cellSpan = Math.round(barEnd.diff(barStart, 'days').days);
        const isoDate = interval.start.toISODate() ?? String(interval.start.toMillis());

        return {
            id: `aggregation-${locationId}-${isoDate}`,
            label: '',
            groupLabel: '',
            phases: [
                {
                    id: `aggregation-phase-${locationId}-${isoDate}`,
                    cellSpan,
                    position: 'Main' as const,
                    specialization: null,
                    color: 'Aggregation' as const,
                    usage: { id: '', position: -1, type: 'Aggregation' as const },
                },
            ],
            startDateTime: barStart,
            endDateTime: barEnd,
            locationId,
            clickAction: { type: 'No-Action' as const, tooltipText: $t('aggregation-expand-hint') },
            linkTarget: '',
            variant: 'Solid' as const,
            isOptional: false,
            allocationType: 'Aggregation' as const,
            allocationVariant: {
                id: 'aggregation',
                index: -1,
                label: 'Aggregation',
                target: '',
                referenceAllocationId: null,
            },
            locationTypeCategory,
        };
    });
}

// Build the expand-independent render data for every location in the tree. This runs the recursive rollup
// (via `computeHierarchyData`) and materializes each location's final aggregation bars and merged conflict
// markers exactly once, so the heavy work does not repeat when the user only expands/collapses rows.
function buildLocationRenderData(
    locations: TimelineLocationData[],
    allocsByLoc: Map<string, Allocation[]>,
    conflictsByLoc: Map<string, Map<string, ConflictMarker>>,
    start: DateTime,
    end: DateTime,
): Map<string, LocationRenderData> {
    const cache = new Map<string, HierarchyComputedData>();
    const renderData = new Map<string, LocationRenderData>();

    function visit(locs: TimelineLocationData[]) {
        for (const location of locs) {
            const data = computeHierarchyData(location, allocsByLoc, conflictsByLoc, start, end, cache);

            renderData.set(location.id, {
                containedAllocations: data.allAllocations,
                conflictsCount: data.conflictsCount,
                icon: {
                    component: getIcon(location.typeCategory, location.type),
                    title: getIconTitle(location.typeCategory, location.type),
                },
                errorMessage: nestedLocationsErrorMessage(location),
                /**
                 * A row always shows the allocations placed on it; only its descendants' are collapsed into
                 * aggregation bars. Nothing can be allocated to buildings, floors or zones, so for them this
                 * is a pure aggregation; it matters for a location that holds allocations and nests others,
                 * like an area with plots.
                 */
                allocations: location.isLeafLocation
                    ? data.directAllocations
                    : [
                          ...data.directAllocations,
                          ...computeAggregationAllocations(
                              data.nestedAllocations,
                              location.id,
                              location.typeCategory,
                              start,
                              end,
                          ),
                      ],
                conflictMarkers: location.isLeafLocation ? data.conflictMarkers : data.allConflictMarkers,
            });

            if (!location.isLeafLocation) {
                visit(location.nestedLocations);
            }
        }
    }

    visit(locations);
    return renderData;
}

// Flatten the hierarchy into a render list, looking up the pre-computed per-location data. All locations
// are emitted (so collapsed rows stay mounted for selection registration); `isVisible` is true only when
// every ancestor is expanded.
function flattenHierarchy(
    locations: TimelineLocationData[],
    parentId: string | undefined,
    renderData: Map<string, LocationRenderData>,
    expandedIds: Set<string>,
    parentVisible: boolean,
    result: HierarchyLocationRowDataBase[],
    getExpandableDisabledExplanation: (id: string) => string | null,
): void {
    for (const location of locations) {
        const data = renderData.get(location.id);
        if (!data) {
            continue;
        }
        const isExpanded = expandedIds.has(location.id);

        result.push({
            id: location.id,
            label: location.label,
            level: location.level,
            typeCategory: location.typeCategory,
            isLeafLocation: location.isLeafLocation,
            isVisible: parentVisible,
            parentId,
            errorMessage: data.errorMessage,
            expandable: location.isLeafLocation
                ? { isExpandable: false as const, conflictsCount: data.conflictsCount }
                : {
                      isExpandable: true as const,
                      isExpanded,
                      nestedConflictsCount: data.conflictsCount,
                      // Only lock an ancestor of the selection source while it is expanded — collapsing
                      // would hide the source, but expanding is always safe. Locking a collapsed row instead
                      // makes the source unreachable, which is what happens after a sections -> hierarchy
                      // switch, where the source's new ancestors start collapsed.
                      expandableDisabledExplanation: isExpanded
                          ? (getExpandableDisabledExplanation(location.id) ?? undefined)
                          : undefined,
                  },
            icon: data.icon,
            linkTarget: location.linkTarget,
            allocations: data.allocations,
            conflictMarkers: data.conflictMarkers,
        });

        // Always include nested locations so their first-column cells stay mounted — required for
        // selection registration during copy. Visibility is controlled via `isVisible` (v-show).
        if (!location.isLeafLocation) {
            flattenHierarchy(
                location.nestedLocations,
                location.id,
                renderData,
                expandedIds,
                parentVisible && isExpanded,
                result,
                getExpandableDisabledExplanation,
            );
        }
    }
}

type HierarchyRowsOptions = {
    locations: Ref<TimelineLocationData[]>;
    allocationsByLocation: Ref<Map<string, Allocation[]>>;
    conflictMarkersByLocation: Ref<Map<string, Map<string, ConflictMarker>>>;
    startDateTime: Ref<DateTime | undefined>;
    endDateTime: Ref<DateTime | undefined>;
    /** When false, no rows are produced (e.g. the sections view is active instead). */
    enabled: Ref<boolean>;
};

/**
 * Builds the flattened hierarchy-view rows (buildings → floors → rooms, zones → areas) with
 * rolled-up conflict counts and aggregation bars for non-leaf rows. Owns the expand state.
 *
 * The per-location render data is computed in a `computed` that depends only on the data and range; a
 * second `computed` assembles the rows from it based on the expand state.
 */
function useHierarchyRowsBase(options: HierarchyRowsOptions): {
    hierarchyRows: ComputedRef<HierarchyLocationRowDataBase[]>;
    containedAllocationsByLocationId: ComputedRef<Map<string, Allocation[]>>;
    expandedLocationIds: Ref<Set<string>>;
    toggleLocationExpanded: (locationId: string) => void;
} {
    const { locations, allocationsByLocation, conflictMarkersByLocation, startDateTime, endDateTime, enabled } =
        options;

    const locationSelectionStore = useLocationSelectionStore();

    const expandedLocationIds = ref(new Set<string>());
    function toggleLocationExpanded(locationId: string) {
        const newSet = new Set(expandedLocationIds.value);
        if (newSet.has(locationId)) {
            newSet.delete(locationId);
        } else {
            newSet.add(locationId);
        }
        expandedLocationIds.value = newSet;
    }

    // Expand-independent: recomputes only when the underlying data or visible range changes.
    const locationRenderData = computed<Map<string, LocationRenderData>>(() => {
        if (!enabled.value) {
            return new Map();
        }
        const start = startDateTime.value;
        const end = endDateTime.value;
        if (!start || !end) {
            return new Map();
        }

        return buildLocationRenderData(
            locations.value,
            allocationsByLocation.value,
            conflictMarkersByLocation.value,
            start,
            end,
        );
    });

    const containedAllocationsByLocationId = computed(
        () => new Map(Array.from(locationRenderData.value, ([id, data]) => [id, data.containedAllocations])),
    );

    // Assembles the rows from the per-location render data for the current expand state.
    const hierarchyRows = computed<HierarchyLocationRowDataBase[]>(() => {
        const renderData = locationRenderData.value;
        if (renderData.size === 0) {
            return [];
        }

        const result: HierarchyLocationRowDataBase[] = [];
        flattenHierarchy(
            locations.value,
            undefined,
            renderData,
            expandedLocationIds.value,
            true,
            result,
            locationSelectionStore.getExpandableDisabledExplanation,
        );
        return result;
    });

    return { hierarchyRows, containedAllocationsByLocationId, expandedLocationIds, toggleLocationExpanded };
}

/**
 * The hierarchy-view rows, each counting the allocations of its location and all nested locations.
 */
export function useHierarchyRows(options: HierarchyRowsOptions): {
    hierarchyRows: ComputedRef<HierarchyLocationRowData[]>;
    expandedLocationIds: Ref<Set<string>>;
    toggleLocationExpanded: (locationId: string) => void;
} {
    const base = useHierarchyRowsBase(options);

    // Independent of the expand state, so expanding or collapsing a row doesn't count again
    const allocationCountByLocationId = computed(() => {
        const start = options.startDateTime.value;
        const end = options.endDateTime.value;
        return start && end
            ? countAllocationsByLocation(base.containedAllocationsByLocationId.value, start, end)
            : undefined;
    });

    // No page renders the timeline before its range is known
    const hierarchyRows = computed(() => {
        const allocationCounts = allocationCountByLocationId.value;
        if (!allocationCounts) {
            return [];
        }
        return base.hierarchyRows.value.map((row) => ({
            ...row,
            allocationCount: requireAllocationCount(allocationCounts, row.id),
        }));
    });

    return {
        hierarchyRows,
        expandedLocationIds: base.expandedLocationIds,
        toggleLocationExpanded: base.toggleLocationExpanded,
    };
}

/**
 * The hierarchy-view rows, each counting the own and the foreign allocations of its location and all nested
 * locations apart.
 */
export function useOwnAndForeignHierarchyRows(
    options: HierarchyRowsOptions & { foreignAllocationIds: Ref<Set<string>> },
): {
    hierarchyRows: ComputedRef<OwnAndForeignHierarchyLocationRowData[]>;
    expandedLocationIds: Ref<Set<string>>;
    toggleLocationExpanded: (locationId: string) => void;
} {
    const base = useHierarchyRowsBase(options);

    // Independent of the expand state, so expanding or collapsing a row doesn't count again
    const allocationCountByLocationId = computed(() => {
        const start = options.startDateTime.value;
        const end = options.endDateTime.value;
        return start && end
            ? countOwnAndForeignAllocationsByLocation(
                  base.containedAllocationsByLocationId.value,
                  options.foreignAllocationIds.value,
                  start,
                  end,
              )
            : undefined;
    });

    // No page renders the timeline before its range is known
    const hierarchyRows = computed(() => {
        const allocationCounts = allocationCountByLocationId.value;
        if (!allocationCounts) {
            return [];
        }
        return base.hierarchyRows.value.map((row) => ({
            ...row,
            allocationCount: requireAllocationCount(allocationCounts, row.id),
        }));
    });

    return {
        hierarchyRows,
        expandedLocationIds: base.expandedLocationIds,
        toggleLocationExpanded: base.toggleLocationExpanded,
    };
}
